import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import {
  BaseResponse,
  EmailLoginBody,
  LinkResponse,
  LoginResponse,
  NewUserBody,
  TokenLoginBody,
  User,
} from "../../types/user"
import {
  Application,
  NewApplicationBody,
  NewApplicationResponse,
  UpdateApplicationBody,
} from "../../types/application"
import {
  OAuthParams,
  OAuthPreflightResponse,
  OAuthRedirectResponse,
  OAuthResponse,
} from "../../types/oauth"
import { Cookies, useCookies } from "react-cookie"
import { store } from "../store/store"

interface JWTPayload {
  user_id: string
  family: string
  scopes: string[]
  exp: number
}

export const serviceApi = createApi({
  reducerPath: "serviceApi",
  refetchOnReconnect: true,
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BACKEND_URL,
    credentials: "include",
    prepareHeaders: async (headers, { endpoint }) => {
      const cookies = new Cookies(null, { path: "/" })

      let jwt = cookies.get("access_token")
      if (jwt) {
        let data
        try {
          const [, payload] = jwt.split(".")
          data = JSON.parse(atob(payload)) as JWTPayload
        } catch (e) {
          data = null
        }

        if (
          data &&
          new Date(data.exp * 1000 + 60000) < new Date() &&
          !["tokenLogin", "emailLogin", "newUser"].includes(endpoint)
        ) {
          const old_refresh = cookies.get("refresh_token")

          try {
            const {
              payload: { access_token, refresh_token },
            } = await store
              .dispatch(
                serviceApi.endpoints!.tokenLogin.initiate({
                  refresh_token: old_refresh,
                  client_id: import.meta.env.VITE_CLIENT_ID,
                  grant_type: "refresh_token",
                }),
              )
              .unwrap()

            cookies.set("access_token", access_token, {
              sameSite: "strict",
              secure: true,
            })
            cookies.set("refresh_token", access_token, {
              sameSite: "strict",
              secure: true,
            })
            jwt = access_token
          } catch (e) {
            console.error("Error logging in with token", e)
            // cookies.remove("access_token")
            // cookies.remove("refresh_token")
          }
        }
        headers.set("Authorization", `Bearer ${jwt}`)
      }
      return headers
    },
  }),
  tagTypes: ["User", "Application", "OAuth"] as const,
  endpoints: (builder) => ({
    getUser: builder.query<BaseResponse<User>, void>({
      query: () => `/user`,
      providesTags: ["User" as const],
    }),
    getUserEnabledApps: builder.query<BaseResponse<User>, void>({
      query: () => `/user/apps`,
      providesTags: ["User" as const],
    }),
    newUser: builder.mutation<BaseResponse<User>, NewUserBody>({
      query: (body) => ({
        url: `/user`,
        method: "POST",
        body,
      }),
      invalidatesTags: [],
    }),
    updateUser: builder.mutation<BaseResponse<User>, Partial<NewUserBody>>({
      query: (body) => ({
        url: `/user`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["User" as const],
      transformResponse(baseQueryReturnValue: BaseResponse<User>, meta, arg) {
        return baseQueryReturnValue
      },
    }),
    deleteUser: builder.mutation<void, void>({
      query: (body) => ({
        url: `/user`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["User" as const],
    }),
    emailLogin: builder.mutation<BaseResponse<LoginResponse>, EmailLoginBody>({
      query: (body) => ({
        url: `/login/email`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["User" as const],
      transformResponse(
        baseQueryReturnValue: BaseResponse<LoginResponse>,
        meta,
        arg,
      ) {
        const cookies = new Cookies(null, { path: "/" })
        if (baseQueryReturnValue.payload.access_token) {
          cookies.set(
            "access_token",
            baseQueryReturnValue.payload.access_token,
            {
              sameSite: "strict",
              secure: true,
            },
          )
          cookies.set(
            "refresh_token",
            baseQueryReturnValue.payload.refresh_token,
            {
              sameSite: "strict",
              secure: true,
            },
          )
        } else {
          cookies.remove("access_token")
          cookies.remove("refresh_token")
        }

        return baseQueryReturnValue
      },
    }),
    tokenLogin: builder.mutation<BaseResponse<LoginResponse>, TokenLoginBody>({
      query: (body) => ({
        url: `/login/token`,
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: new URLSearchParams(body as unknown as Record<string, string>),
      }),
      invalidatesTags: ["User" as const],
      transformResponse(
        baseQueryReturnValue: BaseResponse<LoginResponse>,
        meta,
        arg,
      ) {
        const cookies = new Cookies(null, { path: "/" })
        cookies.set("access_token", baseQueryReturnValue.payload.access_token, {
          sameSite: "strict",
          secure: true,
        })
        cookies.set(
          "refresh_token",
          baseQueryReturnValue.payload.refresh_token,
          {
            sameSite: "strict",
            secure: true,
          },
        )

        return baseQueryReturnValue
      },
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: `/logout`,
        method: "POST",
        headers: {
          refresh_token: new Cookies(null, { path: "/" }).get("refresh_token"),
        },
        body: {
          refresh_token: new Cookies(null, { path: "/" }).get("refresh_token"),
        },
      }),
      transformResponse(baseQueryReturnValue: void, meta, arg) {
        const cookies = new Cookies(null, { path: "/" })
        cookies.remove("access_token")
        cookies.remove("refresh_token")
        return baseQueryReturnValue
      },
      invalidatesTags: ["User" as const],
    }),
    getApplications: builder.query<BaseResponse<Application[]>, void>({
      query: () => `/applications`,
      providesTags: ["Application" as const],
    }),
    getApplicationByID: builder.query<BaseResponse<Application>, string>({
      query: (id) => `/applications/${id}`,
      providesTags: ["Application" as const],
    }),
    newApplication: builder.mutation<
      BaseResponse<NewApplicationResponse>,
      NewApplicationBody
    >({
      query: (body) => ({
        url: `/applications`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Application" as const],
    }),
    deleteApplication: builder.mutation<void, string>({
      query: (client_id) => ({
        url: `/applications/${client_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Application" as const],
    }),
    updateApplication: builder.mutation<
      Application,
      { client_id: string; body: Partial<UpdateApplicationBody> }
    >({
      query: ({ client_id, body }) => ({
        url: `/applications/${client_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Application" as const],
    }),
    oauthPreflight: builder.query<
      BaseResponse<OAuthPreflightResponse>,
      OAuthParams
    >({
      query: (params) => ({
        url: `/oauth/preflight`,
        method: "GET",
        params: params,
      }),
      providesTags: ["OAuth" as const],
    }),
    oauthReview: builder.query<BaseResponse<OAuthResponse>, OAuthParams>({
      query: (params) => ({
        url: `/oauth`,
        method: "GET",
        params: params,
      }),
      providesTags: ["OAuth" as const],
    }),
    authorize: builder.mutation<
      BaseResponse<OAuthRedirectResponse>,
      OAuthResponse
    >({
      query: (body) => ({
        url: `/oauth/authorize`,
        method: "POST",
        body: body,
      }),
    }),
    getLinkCode: builder.query<BaseResponse<LinkResponse>, void>({
      query: () => `/user/link`,
      providesTags: ["User" as const],
    }),
    linkAccount: builder.mutation<BaseResponse<void>, string>({
      query: (account_name) => ({
        url: `/user/link`,
        params: {
          account_name,
        },
        method: "POST",
      }),
      invalidatesTags: ["User" as const],
    }),
  }),
})

export const {
  useGetUserQuery,
  useNewUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useEmailLoginMutation,
  useLogoutMutation,
  useGetApplicationsQuery,
  useGetApplicationByIDQuery,
  useNewApplicationMutation,
  useDeleteApplicationMutation,
  useUpdateApplicationMutation,
  useGetLinkCodeQuery,
  useLinkAccountMutation,
  useOauthPreflightQuery,
  useOauthReviewQuery,
  useAuthorizeMutation,
  useGetUserEnabledAppsQuery,
} = serviceApi

export function useProfile() {
  const [cookies] = useCookies(["access_token", "refresh_token"])
  const { data, isSuccess, isError, ...rest } = useGetUserQuery(undefined, {
    skip: !cookies.access_token,
  })

  return {
    data: cookies.access_token ? data : undefined,
    isSuccess: cookies.access_token ? isSuccess : false,
    isError: cookies.access_token ? isError : true,
    ...rest,
  }
}
